import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { PremiumOverlay } from '../../components/ui/premium-overlay';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency, formatDate } from '../../lib/i18n';
import {
  ArrowLeft,
  Edit,
  Phone,
  MapPin,
  CreditCard,
  UserCircle,
  CalendarDays,
  Banknote,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  Shield,
  Wallet,
  ArrowDownCircle,
  Pencil,
  Save,
  X,
  Image
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  mechanic: { en: 'Mechanic', bn: 'মেকানিক' },
  helper: { en: 'Helper', bn: 'হেল্পার' },
  manager: { en: 'Manager', bn: 'ম্যানেজার' },
  electrician: { en: 'Electrician', bn: 'ইলেকট্রিশিয়ান' },
  painter: { en: 'Painter', bn: 'পেইন্টার' },
  cleaner: { en: 'Cleaner', bn: 'ক্লিনার' },
  accountant: { en: 'Accountant', bn: 'হিসাবরক্ষক' },
  other: { en: 'Other', bn: 'অন্যান্য' },
};

const MONTH_NAMES_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_NAMES_BN = ['জানুয়ারি','ফেব্রুয়ারি','মার্চ','এপ্রিল','মে','জুন','জুলাই','আগস্ট','সেপ্টেম্বর','অক্টোবর','নভেম্বর','ডিসেম্বর'];

const formatMonthLabel = (yyyyMM, lang) => {
  const [y, m] = yyyyMM.split('-');
  const mi = parseInt(m, 10) - 1;
  const name = lang === 'en' ? MONTH_NAMES_EN[mi] : MONTH_NAMES_BN[mi];
  return `${name} ${y}`;
};

export default function EmployeeDetailPage() {
  const { id, garageSlug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isPremium } = useAuth();
  const { toast } = useToast();
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  // Salary month
  const now = new Date();
  const [salaryMonth, setSalaryMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Advance form
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [advanceNote, setAdvanceNote] = useState('');
  const [advanceSaving, setAdvanceSaving] = useState(false);

  // Advance inline edit
  const [editingAdvance, setEditingAdvance] = useState(null); // { id, amount, note }

  // Payment form
  const [showPayForm, setShowPayForm] = useState(false);
  const [payDeductions, setPayDeductions] = useState('');
  const [payBonus, setPayBonus] = useState('');
  const [payNote, setPayNote] = useState('');
  const [paySaving, setPaySaving] = useState(false);

  // Payment inline edit
  const [editingPayment, setEditingPayment] = useState(null); // { id, deductions, bonus, note }

  // NID photo viewer
  const [showNidPhoto, setShowNidPhoto] = useState(null); // 'front' | 'back' | null

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  useEffect(() => {
    if (employee) fetchSummary();
  }, [salaryMonth, employee]);

  const fetchEmployee = async () => {
    try {
      const res = await axios.get(`${API}/employees/${id}`);
      setEmployee(res.data);
    } catch (err) {
      console.error('Failed to fetch employee:', err);
      navigate(`${basePath}/employees`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await axios.get(`${API}/employees/${id}/salary-summary?period_month=${salaryMonth}`);
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const navigateMonth = (dir) => {
    const [y, m] = salaryMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    const ny = d.getFullYear();
    const nm = d.getMonth() + 1;
    if (ny > now.getFullYear() || (ny === now.getFullYear() && nm > now.getMonth() + 1)) return;
    setSalaryMonth(`${ny}-${String(nm).padStart(2, '0')}`);
  };

  // Derived: is salary already paid this month?
  const salaryPaid = summary?.payments?.length > 0;

  // --- Advance CRUD ---
  const addAdvance = async () => {
    const amt = parseFloat(advanceAmount);
    if (!amt || amt <= 0) {
      toast({ title: language === 'en' ? 'Enter a valid amount' : 'সঠিক পরিমাণ লিখুন', variant: 'destructive' });
      return;
    }
    setAdvanceSaving(true);
    // Use today if current month, otherwise 1st of selected month
    const todayStr = new Date().toISOString().slice(0, 7);
    const advDate = salaryMonth === todayStr ? new Date().toISOString().slice(0, 10) : `${salaryMonth}-01`;
    try {
      await axios.post(`${API}/salary-advances`, {
        employee_id: id,
        amount: amt,
        note: advanceNote,
        date: advDate,
      });
      toast({ title: language === 'en' ? 'Advance recorded!' : 'অগ্রিম রেকর্ড হয়েছে!' });
      setAdvanceAmount('');
      setAdvanceNote('');
      fetchSummary();
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    } finally {
      setAdvanceSaving(false);
    }
  };

  const deleteAdvance = async (advId) => {
    if (!window.confirm(language === 'en' ? 'Delete this advance?' : 'এই অগ্রিম মুছে ফেলবেন?')) return;
    try {
      await axios.delete(`${API}/salary-advances/${advId}`);
      toast({ title: language === 'en' ? 'Advance deleted' : 'অগ্রিম মুছে ফেলা হয়েছে' });
      fetchSummary();
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  const saveAdvanceEdit = async () => {
    if (!editingAdvance) return;
    const amt = parseFloat(editingAdvance.amount);
    if (!amt || amt <= 0) return;
    try {
      await axios.put(`${API}/salary-advances/${editingAdvance.id}`, {
        amount: amt,
        note: editingAdvance.note,
      });
      toast({ title: language === 'en' ? 'Advance updated' : 'অগ্রিম আপডেট হয়েছে' });
      setEditingAdvance(null);
      fetchSummary();
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  // --- Payment CRUD ---
  const paySalary = async () => {
    if (!summary) return;
    const deductions = parseFloat(payDeductions) || 0;
    const bonus = parseFloat(payBonus) || 0;
    const netPaid = summary.remaining - deductions + bonus;
    if (netPaid <= 0) {
      toast({ title: language === 'en' ? 'Nothing to pay' : 'পরিশোধ করার কিছু নেই', variant: 'destructive' });
      return;
    }
    setPaySaving(true);
    try {
      await axios.post(`${API}/salary-payments`, {
        employee_id: id,
        period_month: salaryMonth,
        base_salary: summary.base_salary,
        total_advances: summary.total_advances,
        deductions,
        bonus,
        net_paid: netPaid,
        note: payNote,
      });
      toast({ title: language === 'en' ? 'Salary paid!' : 'বেতন প্রদান করা হয়েছে!' });
      setShowPayForm(false);
      setPayDeductions('');
      setPayBonus('');
      setPayNote('');
      fetchSummary();
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    } finally {
      setPaySaving(false);
    }
  };

  const deletePayment = async (payId) => {
    if (!window.confirm(language === 'en' ? 'Delete this payment record?' : 'এই পেমেন্ট রেকর্ড মুছে ফেলবেন?')) return;
    try {
      await axios.delete(`${API}/salary-payments/${payId}`);
      toast({ title: language === 'en' ? 'Payment deleted' : 'পেমেন্ট মুছে ফেলা হয়েছে' });
      fetchSummary();
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  const savePaymentEdit = async () => {
    if (!editingPayment) return;
    const ded = parseFloat(editingPayment.deductions) || 0;
    const bon = parseFloat(editingPayment.bonus) || 0;
    // Recalculate net_paid: original base portion + new bonus - new deductions
    const origPay = summary?.payments?.find(p => p.id === editingPayment.id);
    if (!origPay) return;
    const basePortion = origPay.net_paid - origPay.bonus + origPay.deductions;
    const newNet = basePortion + bon - ded;
    try {
      await axios.put(`${API}/salary-payments/${editingPayment.id}`, {
        deductions: ded,
        bonus: bon,
        net_paid: Math.max(0, newNet),
        note: editingPayment.note,
      });
      toast({ title: language === 'en' ? 'Payment updated' : 'পেমেন্ট আপডেট হয়েছে' });
      setEditingPayment(null);
      fetchSummary();
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  if (loading && isPremium) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      </AdminLayout>
    );
  }

  if (!employee) return null;

  const payDed = parseFloat(payDeductions) || 0;
  const payBon = parseFloat(payBonus) || 0;
  const calculatedNet = summary ? Math.max(0, summary.remaining - payDed + payBon) : 0;

  return (
    <AdminLayout>
      <PremiumOverlay feature={language === 'en' ? 'Employee & Payroll Management' : 'কর্মচারী ও বেতন ব্যবস্থাপনা'} locked={!isPremium}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`${basePath}/employees`)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              {employee.photo ? (
                <img src={employee.photo} alt={employee.name} className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserCircle className="w-8 h-8 text-blue-600" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold font-['Barlow_Condensed']">{employee.name}</h1>
                  {!employee.is_active && <Badge className="bg-red-100 text-red-700">{language === 'en' ? 'Inactive' : 'নিষ্ক্রিয়'}</Badge>}
                </div>
                <p className="text-slate-500">
                  {ROLE_LABELS[employee.role]?.[language] || employee.role} • {language === 'en' ? 'Since' : 'যোগদান'} {employee.join_date || formatDate(employee.created_at, language)}
                </p>
              </div>
            </div>
          </div>
          <Link to={`${basePath}/employees/${id}/edit`}>
            <Button variant="outline" className="gap-2">
              <Edit className="w-4 h-4" />
              {language === 'en' ? 'Edit' : 'সম্পাদনা'}
            </Button>
          </Link>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <Banknote className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
              <p className="text-xl font-bold text-emerald-700">{formatCurrency(employee.monthly_salary || 0, language)}</p>
              <p className="text-xs text-slate-500">{language === 'en' ? 'Monthly Salary' : 'মাসিক বেতন'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <ArrowDownCircle className="w-5 h-5 mx-auto mb-1 text-orange-500" />
              <p className="text-xl font-bold text-orange-600">{formatCurrency(summary?.total_advances || 0, language)}</p>
              <p className="text-xs text-slate-500">{language === 'en' ? 'Advances' : 'অগ্রিম'} ({formatMonthLabel(salaryMonth, language).split(' ')[0]})</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Wallet className="w-5 h-5 mx-auto mb-1 text-rose-500" />
              <p className={`text-xl font-bold ${(summary?.remaining || 0) > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                {formatCurrency(summary?.remaining || 0, language)}
              </p>
              <p className="text-xs text-slate-500">{language === 'en' ? 'Salary Due' : 'বেতন বাকি'}</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Employee Info */}
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle>{language === 'en' ? 'Contact Info' : 'যোগাযোগের তথ্য'}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span>{employee.phone}</span>
                </div>
                {employee.address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{employee.address}</span>
                  </div>
                )}
                {employee.nid && (
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-4 h-4 text-slate-400" />
                    <span>NID: {employee.nid}</span>
                  </div>
                )}
                {(employee.nid_front || employee.nid_back) && (
                  <div className="flex gap-2">
                    {employee.nid_front && (
                      <button onClick={() => setShowNidPhoto('front')} className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                        <Image className="w-3 h-3" />{language === 'en' ? 'NID Front' : 'NID সামনে'}
                      </button>
                    )}
                    {employee.nid_back && (
                      <button onClick={() => setShowNidPhoto('back')} className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                        <Image className="w-3 h-3" />{language === 'en' ? 'NID Back' : 'NID পিছনে'}
                      </button>
                    )}
                  </div>
                )}
                {employee.join_date && (
                  <div className="flex items-center gap-3">
                    <CalendarDays className="w-4 h-4 text-slate-400" />
                    <span>{language === 'en' ? 'Joined' : 'যোগদান'}: {employee.join_date}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* NID Photo Viewer */}
            {showNidPhoto && (
              <Card className="border-blue-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">{showNidPhoto === 'front' ? (language === 'en' ? 'NID Front' : 'NID সামনের দিক') : (language === 'en' ? 'NID Back' : 'NID পিছনের দিক')}</CardTitle>
                    <button onClick={() => setShowNidPhoto(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
                  </div>
                </CardHeader>
                <CardContent>
                  <img src={showNidPhoto === 'front' ? employee.nid_front : employee.nid_back} alt="NID" className="w-full rounded-lg" />
                </CardContent>
              </Card>
            )}

            {(employee.emergency_contact || employee.emergency_phone) && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Shield className="w-4 h-4 text-red-500" />{language === 'en' ? 'Emergency Contact' : 'জরুরি যোগাযোগ'}</CardTitle></CardHeader>
                <CardContent>
                  {employee.emergency_contact && <p className="font-medium">{employee.emergency_contact}</p>}
                  {employee.emergency_phone && <p className="text-slate-500">{employee.emergency_phone}</p>}
                </CardContent>
              </Card>
            )}

            {employee.notes && (
              <Card>
                <CardHeader><CardTitle>{language === 'en' ? 'Notes' : 'নোট'}</CardTitle></CardHeader>
                <CardContent><p className="text-slate-600">{employee.notes}</p></CardContent>
              </Card>
            )}
          </div>

          {/* Right column: Salary Management */}
          <div className="lg:col-span-2 space-y-6">
            {/* Month Navigator */}
            <div className="flex items-center justify-center gap-3 bg-white border rounded-lg p-3">
              <button onClick={() => navigateMonth(-1)} className="p-1 hover:bg-slate-100 rounded">
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <span className="text-lg font-semibold text-slate-800 min-w-[180px] text-center">
                {formatMonthLabel(salaryMonth, language)}
              </span>
              <button onClick={() => navigateMonth(1)} className="p-1 hover:bg-slate-100 rounded">
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            {/* Quick Advance — hidden when salary already paid */}
            {!salaryPaid && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowDownCircle className="w-5 h-5 text-orange-500" />
                    {language === 'en' ? 'Record Advance / বকেয়া' : 'অগ্রিম রেকর্ড করুন'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      placeholder={language === 'en' ? 'Amount (৳)' : 'পরিমাণ (৳)'}
                      value={advanceAmount}
                      onChange={e => setAdvanceAmount(e.target.value)}
                      className="w-32"
                    />
                    <Input
                      placeholder={language === 'en' ? 'Note (optional)' : 'নোট (ঐচ্ছিক)'}
                      value={advanceNote}
                      onChange={e => setAdvanceNote(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      onClick={addAdvance}
                      disabled={advanceSaving}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      {advanceSaving ? <Spinner size="sm" /> : <Plus className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Advances List */}
            {summaryLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : (
              <>
                {summary?.advances?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm text-slate-600">
                        {language === 'en' ? 'Advances This Month' : 'এই মাসের অগ্রিম'} ({summary.advances.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {summary.advances.map(adv => (
                          <div key={adv.id} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                            {editingAdvance?.id === adv.id ? (
                              <>
                                <div className="flex items-center gap-2 flex-1">
                                  <Input
                                    type="number" min="0"
                                    value={editingAdvance.amount}
                                    onChange={e => setEditingAdvance(p => ({...p, amount: e.target.value}))}
                                    className="w-24 h-7 text-sm"
                                  />
                                  <Input
                                    value={editingAdvance.note}
                                    onChange={e => setEditingAdvance(p => ({...p, note: e.target.value}))}
                                    className="flex-1 h-7 text-sm"
                                    placeholder="Note"
                                  />
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                  <button onClick={saveAdvanceEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Save className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => setEditingAdvance(null)} className="p-1 hover:bg-slate-100 rounded text-slate-400"><X className="w-3.5 h-3.5" /></button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-3">
                                  <span className="font-bold text-orange-700">{formatCurrency(adv.amount, language)}</span>
                                  {adv.note && <span className="text-sm text-slate-500">— {adv.note}</span>}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-slate-400 mr-1">{adv.date}</span>
                                  {!salaryPaid && (
                                    <>
                                      <button onClick={() => setEditingAdvance({ id: adv.id, amount: adv.amount, note: adv.note || '' })} className="p-1 hover:bg-blue-100 rounded text-blue-400 hover:text-blue-600">
                                        <Pencil className="w-3 h-3" />
                                      </button>
                                      <button onClick={() => deleteAdvance(adv.id)} className="p-1 hover:bg-red-100 rounded text-red-400 hover:text-red-600">
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                        <div className="flex justify-between pt-2 border-t font-medium">
                          <span className="text-slate-600">{language === 'en' ? 'Total Advances' : 'মোট অগ্রিম'}</span>
                          <span className="text-orange-700">{formatCurrency(summary.total_advances, language)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Salary Payment (single) */}
                {summary?.payments?.length > 0 && (() => {
                  const pay = summary.payments[0];
                  return (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm text-slate-600">
                          {language === 'en' ? 'Salary Payment' : 'বেতন প্রদান'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {editingPayment?.id === pay.id ? (
                            <div className="space-y-2">
                              <div className="grid grid-cols-3 gap-2">
                                <div>
                                  <Label className="text-xs">{language === 'en' ? 'Deductions' : 'কর্তন'}</Label>
                                  <Input type="number" min="0" value={editingPayment.deductions}
                                    onChange={e => setEditingPayment(p => ({...p, deductions: e.target.value}))}
                                    onFocus={e => { if (e.target.value === '0') setEditingPayment(p => ({...p, deductions: ''})); }}
                                    className="h-7 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-xs">{language === 'en' ? 'Bonus' : 'বোনাস'}</Label>
                                  <Input type="number" min="0" value={editingPayment.bonus}
                                    onChange={e => setEditingPayment(p => ({...p, bonus: e.target.value}))}
                                    onFocus={e => { if (e.target.value === '0') setEditingPayment(p => ({...p, bonus: ''})); }}
                                    className="h-7 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-xs">{language === 'en' ? 'Note' : 'নোট'}</Label>
                                  <Input value={editingPayment.note}
                                    onChange={e => setEditingPayment(p => ({...p, note: e.target.value}))}
                                    className="h-7 text-sm" />
                                </div>
                              </div>
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="ghost" onClick={() => setEditingPayment(null)} className="h-6 px-2 text-xs"><X className="w-3 h-3 mr-1" />{language === 'en' ? 'Cancel' : 'বাতিল'}</Button>
                                <Button size="sm" onClick={savePaymentEdit} className="h-6 px-2 text-xs bg-blue-600 hover:bg-blue-700"><Save className="w-3 h-3 mr-1" />{language === 'en' ? 'Save' : 'সেভ'}</Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                              <div>
                                <span className="font-bold text-blue-700">{formatCurrency(pay.net_paid, language)}</span>
                                {pay.note && <span className="text-sm text-slate-500 ml-2">— {pay.note}</span>}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-400 mr-1">{formatDate(pay.paid_at, language)}</span>
                                <button onClick={() => setEditingPayment({ id: pay.id, deductions: String(pay.deductions || 0), bonus: String(pay.bonus || 0), note: pay.note || '' })} className="p-1 hover:bg-blue-100 rounded text-blue-400 hover:text-blue-600">
                                  <Pencil className="w-3 h-3" />
                                </button>
                                <button onClick={() => deletePayment(pay.id)} className="p-1 hover:bg-red-100 rounded text-red-400 hover:text-red-600">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          )}
                          {/* Bonus / Deductions breakdown */}
                          {(pay.bonus > 0 || pay.deductions > 0) && (
                            <div className="pt-2 border-t text-xs text-slate-500 flex gap-4">
                              {pay.bonus > 0 && <span>{language === 'en' ? 'Bonus' : 'বোনাস'}: <span className="font-medium text-green-600">{formatCurrency(pay.bonus, language)}</span></span>}
                              {pay.deductions > 0 && <span>{language === 'en' ? 'Deductions' : 'কর্তন'}: <span className="font-medium text-red-600">{formatCurrency(pay.deductions, language)}</span></span>}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Pay Salary — only if no payment yet */}
                {summary && summary.remaining > 0 && !salaryPaid && (
                  <Card className="border-emerald-200">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-emerald-700">
                          <Banknote className="w-5 h-5" />
                          {language === 'en' ? 'Pay Salary' : 'বেতন দিন'}
                        </CardTitle>
                        {!showPayForm && (
                          <Button onClick={() => setShowPayForm(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                            {language === 'en' ? 'Pay Now' : 'এখন দিন'}
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    {showPayForm && (
                      <CardContent className="space-y-4">
                        {/* Salary Breakdown */}
                        <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                          <div className="flex justify-between"><span>{language === 'en' ? 'Base Salary' : 'মূল বেতন'}</span><span className="font-medium">{formatCurrency(summary.base_salary, language)}</span></div>
                          <div className="flex justify-between text-orange-600"><span>{language === 'en' ? '(-) Advances' : '(-) অগ্রিম'}</span><span>-{formatCurrency(summary.total_advances, language)}</span></div>
                          {summary.salary_paid > 0 && <div className="flex justify-between text-blue-600"><span>{language === 'en' ? '(-) Salary Paid' : '(-) বেতন প্রদত্ত'}</span><span>-{formatCurrency(summary.salary_paid, language)}</span></div>}
                          <div className="flex justify-between font-bold border-t pt-1"><span>{language === 'en' ? 'Salary Due' : 'বেতন বাকি'}</span><span>{formatCurrency(summary.remaining, language)}</span></div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">{language === 'en' ? 'Deductions (৳)' : 'কর্তন (৳)'}</Label>
                            <Input type="number" min="0" value={payDeductions}
                              onChange={e => setPayDeductions(e.target.value)}
                              onFocus={e => { if (e.target.value === '0') setPayDeductions(''); }}
                              placeholder="0" />
                          </div>
                          <div>
                            <Label className="text-xs">{language === 'en' ? 'Bonus (৳)' : 'বোনাস (৳)'}</Label>
                            <Input type="number" min="0" value={payBonus}
                              onChange={e => setPayBonus(e.target.value)}
                              onFocus={e => { if (e.target.value === '0') setPayBonus(''); }}
                              placeholder="0" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Note' : 'নোট'}</Label>
                          <Input value={payNote} onChange={e => setPayNote(e.target.value)} placeholder={language === 'en' ? 'e.g. February salary' : 'যেমন: ফেব্রুয়ারি বেতন'} />
                        </div>

                        <div className="flex items-center justify-between bg-emerald-50 rounded-lg p-3">
                          <span className="font-semibold text-emerald-800">{language === 'en' ? 'Net to Pay' : 'মোট প্রদেয়'}</span>
                          <span className="text-2xl font-bold text-emerald-700">{formatCurrency(calculatedNet, language)}</span>
                        </div>

                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" onClick={() => setShowPayForm(false)}>
                            {language === 'en' ? 'Cancel' : 'বাতিল'}
                          </Button>
                          <Button onClick={paySalary} disabled={paySaving || calculatedNet <= 0} className="bg-emerald-600 hover:bg-emerald-700">
                            {paySaving ? <Spinner size="sm" className="mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                            {language === 'en' ? `Pay ${formatCurrency(calculatedNet, language)}` : `${formatCurrency(calculatedNet, language)} দিন`}
                          </Button>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )}

                {/* All caught up */}
                {summary && summary.remaining <= 0 && !summary?.payments?.length && (
                  <Card className="border-slate-200">
                    <CardContent className="p-6 text-center">
                      <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">
                        {language === 'en' ? 'No salary due for this month' : 'এই মাসে কোন বেতন বাকি নেই'}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {summary && summary.remaining <= 0 && summary?.payments?.length > 0 && (
                  <div className="text-center py-3">
                    <Badge className="bg-green-100 text-green-800 text-sm px-4 py-1">
                      <CheckCircle className="w-4 h-4 mr-1 inline" />
                      {language === 'en' ? 'Salary fully paid for this month!' : 'এই মাসের বেতন সম্পূর্ণ প্রদান করা হয়েছে!'}
                    </Badge>
                  </div>
                )}

                {/* Total paid for the month */}
                {summary && (summary.total_paid > 0 || summary.total_advances > 0) && (
                  <p className="text-center text-sm text-slate-500">
                    {language === 'en' ? 'Total disbursed this month' : 'এই মাসে মোট বিতরিত'}: <span className="font-semibold text-slate-700">{formatCurrency((summary.total_paid || 0) + (summary.total_advances || 0), language)}</span>
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      </PremiumOverlay>
    </AdminLayout>
  );
}
